import React, { useState } from 'react';
import axios from 'axios';
import { API, useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Trash2, AlertTriangle, CheckCircle } from 'lucide-react';

export default function DataDeletion() {
  const { currentColors } = useTheme();
  const { user, token, logout } = useAuth();
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleDeleteRequest = async (e) => {
    e.preventDefault();
    setError('');

    if (user) {
      // Logged in user - delete account directly
      if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
        return;
      }
      
      setLoading(true);
      try {
        await axios.delete(`${API}/auth/delete-account`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess(true);
        setTimeout(() => logout(), 3000);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to delete account');
      } finally {
        setLoading(false);
      }
    } else {
      // Not logged in - submit deletion request
      if (email !== confirmEmail) {
        setError('Email addresses do not match');
        return;
      }
      
      setLoading(true);
      try {
        await axios.post(`${API}/data-deletion-request`, {
          email,
          reason
        });
        setSuccess(true);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to submit request');
      } finally {
        setLoading(false);
      }
    }
  };

  if (success) {
    return (
      <div className="max-w-xl mx-auto py-12 px-4">
        <div className="glass-card p-8 text-center">
          <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#22c55e' }} />
          <h2 className="text-2xl font-cinzel mb-4" style={{ color: currentColors.primary }}>
            {user ? 'Account Deleted' : 'Request Submitted'}
          </h2>
          <p style={{ color: currentColors.text }}>
            {user 
              ? 'Your account and all associated data have been deleted. You will be logged out shortly.'
              : 'We have received your data deletion request. We will process it within 30 days and send confirmation to your email.'
            }
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <div className="glass-card p-8">
        <div className="flex items-center gap-3 mb-6">
          <Trash2 className="w-8 h-8" style={{ color: currentColors.primary }} />
          <h1 className="text-2xl font-cinzel" style={{ color: currentColors.primary }}>
            Data Deletion Request
          </h1>
        </div>

        <div 
          className="p-4 rounded-xl mb-6 flex items-start gap-3"
          style={{ backgroundColor: 'rgba(251, 191, 36, 0.1)', border: '1px solid rgba(251, 191, 36, 0.3)' }}
        >
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-500">Important</p>
            <p className="text-sm" style={{ color: currentColors.text }}>
              Deleting your data will permanently remove:
            </p>
            <ul className="text-sm mt-2 list-disc pl-4" style={{ color: currentColors.textMuted }}>
              <li>Your account and profile information</li>
              <li>All journal entries and mood data</li>
              <li>Meditation history and progress</li>
              <li>Connected social media accounts</li>
              <li>All posts and content you've created</li>
            </ul>
            <p className="text-sm mt-2 font-medium text-amber-500">
              This action cannot be undone.
            </p>
          </div>
        </div>

        {error && (
          <div 
            className="p-4 rounded-xl mb-6"
            style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}
          >
            <p className="text-red-500">{error}</p>
          </div>
        )}

        <form onSubmit={handleDeleteRequest} className="space-y-4">
          {user ? (
            <div 
              className="p-4 rounded-xl"
              style={{ backgroundColor: `${currentColors.primary}10`, border: `1px solid ${currentColors.primary}30` }}
            >
              <p style={{ color: currentColors.text }}>
                You are logged in as <strong>{user.email}</strong>
              </p>
              <p className="text-sm mt-1" style={{ color: currentColors.textMuted }}>
                Click the button below to permanently delete your account and all data.
              </p>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                  Email Address
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full cosmic-input"
                />
              </div>
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                  Confirm Email Address
                </label>
                <input
                  type="email"
                  required
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  placeholder="Confirm your email"
                  className="w-full cosmic-input"
                />
              </div>
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                  Reason for deletion (optional)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Help us improve by sharing why you're leaving..."
                  rows={3}
                  className="w-full cosmic-input resize-none"
                />
              </div>
            </>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all disabled:opacity-50"
            style={{ backgroundColor: '#dc2626', color: 'white' }}
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Trash2 className="w-5 h-5" />
                {user ? 'Delete My Account' : 'Submit Deletion Request'}
              </>
            )}
          </button>
        </form>

        <p className="text-xs text-center mt-6" style={{ color: currentColors.textMuted }}>
          For questions about data deletion, contact us at support@theaura-space.com
        </p>
      </div>
    </div>
  );
}
